import { useState, useEffect } from 'react';
import { Save, Settings, Clock, Scroll, Mail, MousePointer, List, ShoppingBag, Check, Copy, Code, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { adminGetSettings, adminUpdateSettings } from '@/lib/api';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await adminGetSettings();
      setSettings(data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminUpdateSettings(settings);
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings({ ...settings, [key]: value });
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    toast.success(`${label} copied to clipboard`);
    setTimeout(() => setCopied(''), 2000);
  };

  const embedCode = `<!-- Supplement Finder Widget -->
<div id="supplement-finder-widget"></div>
<script>
(function() {
  var iframe = document.createElement('iframe');
  iframe.src = '${BACKEND_URL?.replace('/api', '')}/embed?source=shopify';
  iframe.style.width = '100%';
  iframe.style.height = '100vh';
  iframe.style.border = 'none';
  iframe.style.minHeight = '700px';
  document.getElementById('supplement-finder-widget').appendChild(iframe);
})();
</script>`;

  const bannerTriggerCode = `<!-- Supplement Finder Banner Trigger -->
<a href="#" onclick="window.openSupplementFinder(); return false;" 
   style="display: inline-block; padding: 12px 24px; background: #4A6763; color: white; border-radius: 50px; text-decoration: none; font-weight: 500;">
  Find My Supplements
</a>
<script>
window.openSupplementFinder = function() {
  var popup = window.open('${BACKEND_URL?.replace('/api', '')}/embed?source=banner', 'SupplementFinder', 'width=600,height=800,scrollbars=yes');
};
</script>`;

  const popupTriggerCode = `<!-- Supplement Finder Popup Trigger -->
<script>
(function() {
  var settings = {
    timeDelay: ${settings?.popup_time_delay || 8},
    scrollTrigger: ${settings?.popup_scroll_trigger || 50},
    exitIntent: ${settings?.popup_exit_intent || true},
    enabled: ${settings?.popup_enabled || true}
  };
  
  if (!settings.enabled) return;
  
  var triggered = false;
  var popupUrl = '${BACKEND_URL?.replace('/api', '')}/embed?source=popup';
  
  function showPopup() {
    if (triggered) return;
    triggered = true;
    localStorage.setItem('supplementFinderShown', Date.now());
    
    var overlay = document.createElement('div');
    overlay.id = 'sf-overlay';
    overlay.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.5);z-index:99999;display:flex;align-items:center;justify-content:center;';
    
    var modal = document.createElement('div');
    modal.style.cssText = 'background:white;border-radius:16px;width:95%;max-width:500px;max-height:90vh;overflow:hidden;position:relative;';
    
    var closeBtn = document.createElement('button');
    closeBtn.innerHTML = '&times;';
    closeBtn.style.cssText = 'position:absolute;top:10px;right:15px;font-size:24px;background:none;border:none;cursor:pointer;z-index:1;color:#666;';
    closeBtn.onclick = function() { document.body.removeChild(overlay); };
    
    var iframe = document.createElement('iframe');
    iframe.src = popupUrl;
    iframe.style.cssText = 'width:100%;height:80vh;border:none;';
    
    modal.appendChild(closeBtn);
    modal.appendChild(iframe);
    overlay.appendChild(modal);
    overlay.onclick = function(e) { if(e.target === overlay) document.body.removeChild(overlay); };
    document.body.appendChild(overlay);
  }
  
  // Time delay trigger
  if (settings.timeDelay > 0) {
    setTimeout(showPopup, settings.timeDelay * 1000);
  }
  
  // Scroll trigger
  if (settings.scrollTrigger > 0) {
    window.addEventListener('scroll', function() {
      var scrollPercent = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;
      if (scrollPercent >= settings.scrollTrigger) showPopup();
    });
  }
  
  // Exit intent (desktop only)
  if (settings.exitIntent && !/Mobi|Android/i.test(navigator.userAgent)) {
    document.addEventListener('mouseout', function(e) {
      if (e.clientY < 10) showPopup();
    });
  }
})();
</script>`;

  const homepageBtnCode = `<!-- Homepage Find Supplements Button -->
<a href="/pages/find-my-supplements" 
   style="display: inline-flex; align-items: center; gap: 8px; padding: 16px 32px; background: #4A6763; color: white; border-radius: 50px; text-decoration: none; font-weight: 500; font-size: 16px;">
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
    <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
    <path d="M2 17l10 5 10-5"></path>
    <path d="M2 12l10 5 10-5"></path>
  </svg>
  Find My Supplements
</a>`;

  const productPageCode = `<!-- Product Page Quiz CTA -->
<div style="background: #F5F5F0; padding: 24px; border-radius: 12px; text-align: center; margin: 20px 0;">
  <p style="margin: 0 0 12px 0; color: #2C2C2C; font-size: 16px;">Not sure if this is right for you?</p>
  <a href="/pages/find-my-supplements" 
     style="display: inline-block; padding: 12px 24px; background: #4A6763; color: white; border-radius: 50px; text-decoration: none; font-weight: 500;">
    Take Our Quiz
  </a>
</div>`;

  if (loading) {
    return <div className="text-stone-400 p-8">Loading settings...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl text-[#2C2C2C] mb-1">Settings</h1>
          <p className="text-stone-600 text-sm">Configure quiz behavior and Shopify integration</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-settings-btn">
          <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <Tabs defaultValue="popup" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="popup">Popup Settings</TabsTrigger>
          <TabsTrigger value="quiz">Quiz Settings</TabsTrigger>
          <TabsTrigger value="shopify">Shopify</TabsTrigger>
          <TabsTrigger value="embed">Embed Codes</TabsTrigger>
        </TabsList>

        {/* Popup Settings */}
        <TabsContent value="popup" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg font-serif">
                <Settings className="w-5 h-5 text-[#4A6763]" strokeWidth={1.5} />
                Popup Trigger Settings
              </CardTitle>
              <CardDescription>
                Configure when the supplement finder popup appears
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base">Enable Popup</Label>
                  <p className="text-sm text-stone-500">Show the quiz popup to visitors</p>
                </div>
                <Switch
                  checked={settings?.popup_enabled}
                  onCheckedChange={(checked) => updateSetting('popup_enabled', checked)}
                  data-testid="popup-enabled-switch"
                />
              </div>

              <div className="border-t pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-5 h-5 text-[#4A6763]" strokeWidth={1.5} />
                  <Label className="text-base">Time Delay Trigger</Label>
                </div>
                <div className="flex items-center gap-4">
                  <Input
                    type="number"
                    min="0"
                    max="120"
                    value={settings?.popup_time_delay || 8}
                    onChange={(e) => updateSetting('popup_time_delay', parseInt(e.target.value) || 0)}
                    className="w-24"
                    data-testid="popup-time-delay-input"
                  />
                  <span className="text-stone-600">seconds after page load</span>
                </div>
                <p className="text-sm text-stone-500 mt-2">
                  Especially important for mobile users. Set to 0 to disable.
                </p>
              </div>

              <div className="border-t pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Scroll className="w-5 h-5 text-[#4A6763]" strokeWidth={1.5} />
                  <Label className="text-base">Scroll Trigger</Label>
                </div>
                <div className="flex items-center gap-4">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={settings?.popup_scroll_trigger || 50}
                    onChange={(e) => updateSetting('popup_scroll_trigger', parseInt(e.target.value) || 0)}
                    className="w-24"
                    data-testid="popup-scroll-input"
                  />
                  <span className="text-stone-600">% of page scrolled</span>
                </div>
                <p className="text-sm text-stone-500 mt-2">
                  Important for mobile. Set to 0 to disable.
                </p>
              </div>

              <div className="border-t pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MousePointer className="w-5 h-5 text-[#4A6763]" strokeWidth={1.5} />
                    <div>
                      <Label className="text-base">Exit Intent Trigger</Label>
                      <p className="text-sm text-stone-500">Show when user moves mouse to leave (desktop only)</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings?.popup_exit_intent}
                    onCheckedChange={(checked) => updateSetting('popup_exit_intent', checked)}
                    data-testid="popup-exit-intent-switch"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Quiz Settings */}
        <TabsContent value="quiz" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg font-serif">
                <Mail className="w-5 h-5 text-[#4A6763]" strokeWidth={1.5} />
                Email Capture
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base">Require Email</Label>
                  <p className="text-sm text-stone-500">Make email required before showing results</p>
                </div>
                <Switch
                  checked={settings?.email_capture_required}
                  onCheckedChange={(checked) => updateSetting('email_capture_required', checked)}
                  data-testid="email-required-switch"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg font-serif">
                <List className="w-5 h-5 text-[#4A6763]" strokeWidth={1.5} />
                Multi-Protocol Results
              </CardTitle>
              <CardDescription>
                Control how many protocols are shown and the scoring thresholds
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="text-base mb-2 block">Maximum Protocols to Show</Label>
                <div className="flex gap-3">
                  {[1, 2, 3].map(n => (
                    <Button
                      key={n}
                      variant={settings?.show_top_protocols === n ? 'default' : 'outline'}
                      onClick={() => updateSetting('show_top_protocols', n)}
                      className={settings?.show_top_protocols === n ? 'btn-primary' : ''}
                      data-testid={`show-${n}-protocol-btn`}
                    >
                      {n === 1 ? '1 Protocol' : `Up to ${n}`}
                    </Button>
                  ))}
                </div>
                <p className="text-sm text-stone-500 mt-2">
                  Maximum number of protocols to show. Actual number depends on scoring.
                </p>
              </div>

              <div className="border-t pt-6">
                <Label className="text-base mb-2 block">Score Threshold (%)</Label>
                <div className="flex items-center gap-4">
                  <Input
                    type="number"
                    min="0.1"
                    max="1.0"
                    step="0.1"
                    value={settings?.multi_protocol_threshold || 0.6}
                    onChange={(e) => updateSetting('multi_protocol_threshold', parseFloat(e.target.value) || 0.6)}
                    className="w-24"
                    data-testid="multi-protocol-threshold-input"
                  />
                  <span className="text-stone-600">of top score</span>
                </div>
                <p className="text-sm text-stone-500 mt-2">
                  A protocol is shown if its condition score is at least this percentage of the highest score. 
                  Lower = more protocols shown. Example: 0.6 means conditions scoring 60%+ of the top score are included.
                </p>
              </div>

              <div className="border-t pt-6">
                <Label className="text-base mb-2 block">Minimum Score</Label>
                <div className="flex items-center gap-4">
                  <Input
                    type="number"
                    min="1"
                    max="20"
                    value={settings?.multi_protocol_min_score || 2}
                    onChange={(e) => updateSetting('multi_protocol_min_score', parseInt(e.target.value) || 2)}
                    className="w-24"
                    data-testid="multi-protocol-min-score-input"
                  />
                  <span className="text-stone-600">minimum points</span>
                </div>
                <p className="text-sm text-stone-500 mt-2">
                  Conditions must score at least this many points to be considered. 
                  Prevents showing protocols for barely-scored conditions.
                </p>
              </div>

              <div className="border-t pt-6">
                <Label className="text-base mb-2 block">Cross-Sell Products</Label>
                <div className="flex items-center gap-4">
                  <Input
                    type="number"
                    min="0"
                    max="10"
                    value={settings?.max_cross_sell_products || 5}
                    onChange={(e) => updateSetting('max_cross_sell_products', parseInt(e.target.value) || 5)}
                    className="w-24"
                    data-testid="max-cross-sell-input"
                  />
                  <span className="text-stone-600">maximum shown</span>
                </div>
                <p className="text-sm text-stone-500 mt-2">
                  How many "Recommended For You" cross-sell products to show below protocol recommendations.
                  Set to 0 to disable cross-sell section.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Shopify Settings */}
        <TabsContent value="shopify" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg font-serif">
                <ShoppingBag className="w-5 h-5 text-[#4A6763]" strokeWidth={1.5} />
                Shopify Store Connection
              </CardTitle>
              <CardDescription>
                Connect to your Shopify store to pull real product data and enable checkout
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="text-base mb-2 block">Store Domain</Label>
                <Input
                  value={settings?.shopify_store_domain || ''}
                  onChange={(e) => updateSetting('shopify_store_domain', e.target.value)}
                  placeholder="yourstore.myshopify.com or yourstore.com"
                  data-testid="shopify-domain-input"
                />
                <p className="text-sm text-stone-500 mt-2">
                  Your Shopify store domain (without https://)
                </p>
              </div>

              <div>
                <Label className="text-base mb-2 block">Storefront API Access Token</Label>
                <Input
                  type="password"
                  value={settings?.shopify_storefront_token || ''}
                  onChange={(e) => updateSetting('shopify_storefront_token', e.target.value)}
                  placeholder="shpat_xxxxxxxxxxxxxxxx"
                  data-testid="shopify-token-input"
                />
                <p className="text-sm text-stone-500 mt-2">
                  Get this from: Shopify Admin → Settings → Apps and sales channels → Develop apps
                </p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  <strong>Required API Scopes:</strong>
                </p>
                <ul className="text-sm text-amber-700 mt-2 list-disc ml-4 space-y-1">
                  <li>unauthenticated_read_product_listings</li>
                  <li>unauthenticated_read_checkouts</li>
                  <li>unauthenticated_write_checkouts</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg font-serif">
                <RefreshCw className="w-5 h-5 text-[#4A6763]" strokeWidth={1.5} />
                Product Auto-Sync
              </CardTitle>
              <CardDescription>
                Automatically sync your product catalog from Shopify on a schedule
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="text-base mb-3 block">Sync Frequency</Label>
                <div className="flex gap-3">
                  {[
                    { value: 'manual', label: 'Manual Only' },
                    { value: 'daily', label: 'Daily' },
                    { value: 'weekly', label: 'Weekly' },
                  ].map(opt => (
                    <Button
                      key={opt.value}
                      variant={settings?.auto_sync_frequency === opt.value ? 'default' : 'outline'}
                      onClick={() => updateSetting('auto_sync_frequency', opt.value)}
                      className={settings?.auto_sync_frequency === opt.value ? 'btn-primary' : ''}
                      data-testid={`sync-freq-${opt.value}-btn`}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
                <p className="text-sm text-stone-500 mt-2">
                  {settings?.auto_sync_frequency === 'daily'
                    ? 'Products will sync from Shopify once per day automatically.'
                    : settings?.auto_sync_frequency === 'weekly'
                    ? 'Products will sync from Shopify once per week automatically.'
                    : 'Products are only synced when you click "Sync from Shopify" on the Products page.'}
                </p>
              </div>

              {settings?.last_sync_at && (
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="text-xs text-stone-500">Last synced</p>
                  <p className="text-sm text-stone-700" data-testid="last-sync-time">
                    {new Date(settings.last_sync_at).toLocaleString()}
                  </p>
                </div>
              )}

              <p className="text-xs text-stone-400">
                Manual sync is always available from Admin &rarr; Products regardless of this setting.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Embed Codes */}
        <TabsContent value="embed" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg font-serif">
                <Code className="w-5 h-5 text-[#4A6763]" strokeWidth={1.5} />
                Shopify Page Embed
              </CardTitle>
              <CardDescription>
                Add this to your "Find My Supplements" page in Shopify
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <pre className="bg-stone-50 p-4 rounded-lg text-xs overflow-x-auto">
                  {embedCode}
                </pre>
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={() => copyToClipboard(embedCode, 'Page Embed')}
                >
                  {copied === 'Page Embed' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-serif">Homepage Button</CardTitle>
              <CardDescription>
                Add a "Find My Supplements" button to your homepage
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <pre className="bg-stone-50 p-4 rounded-lg text-xs overflow-x-auto">
                  {homepageBtnCode}
                </pre>
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={() => copyToClipboard(homepageBtnCode, 'Homepage Button')}
                >
                  {copied === 'Homepage Button' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-serif">Product Page CTA</CardTitle>
              <CardDescription>
                Add a quiz prompt to product pages
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <pre className="bg-stone-50 p-4 rounded-lg text-xs overflow-x-auto">
                  {productPageCode}
                </pre>
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={() => copyToClipboard(productPageCode, 'Product CTA')}
                >
                  {copied === 'Product CTA' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-serif">Banner Trigger Button</CardTitle>
              <CardDescription>
                Opens quiz in a popup window
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <pre className="bg-stone-50 p-4 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap">
                  {bannerTriggerCode}
                </pre>
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={() => copyToClipboard(bannerTriggerCode, 'Banner Trigger')}
                >
                  {copied === 'Banner Trigger' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-serif">Popup Trigger Script</CardTitle>
              <CardDescription>
                Auto-show popup based on time, scroll, or exit intent. Add to theme.liquid.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <pre className="bg-stone-50 p-4 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap max-h-64">
                  {popupTriggerCode}
                </pre>
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={() => copyToClipboard(popupTriggerCode, 'Popup Script')}
                >
                  {copied === 'Popup Script' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminSettings;
